import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { getAccessToken } from "../services/api";
import { loginAdmin, logoutAdmin } from "../services/auth";

/**
 * AuthContext — wraps the super-admin auth flow against the backend's `/auth/admin/login`.
 * The session is just the JWT in localStorage plus a synthesised user object (we don't fetch
 * /auth/me since the admin profile is not interesting beyond "you're signed in").
 */
const AuthContext = createContext(null);
const USER_KEY = "uda2.user";

export function AuthProvider({ children }) {
  const [token, setToken] = useState(getAccessToken());
  const [user, setUser] = useState(() => {
    const raw = localStorage.getItem(USER_KEY);
    try {
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    if (user) localStorage.setItem(USER_KEY, JSON.stringify(user));
    else localStorage.removeItem(USER_KEY);
  }, [user]);

  const login = useCallback(async ({ username, password }) => {
    const data = await loginAdmin({ username, password });
    setToken(data?.accessToken || null);
    setUser({ name: username, role: "Super Admin" });
    return data;
  }, []);

  const logout = useCallback(async () => {
    await logoutAdmin();
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo(
    () => ({
      token,
      user,
      isAuthenticated: Boolean(token),
      bootstrapping: false,
      login,
      logout,
    }),
    [token, user, login, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return ctx;
}
